/**
 * The one place a vendor's display name is written.
 *
 * These names were literals in 29 files across six features, behind six
 * separate label maps that each covered a different subset — which is how the
 * product ended up spelling one vendor two ways. Fixing the spellings without
 * fixing that would just reset the clock: SD-12588 corrected "HouseCall Pro"
 * and "ServiceFusion" inside the Integrations feature and still took four
 * review findings, each another literal the sweep had missed.
 *
 * Deliberately a leaf module with no imports, so features that already depend
 * on each other can read from it without an import cycle.
 *
 * Keys are the integration `type` the backend uses, so a call site that has an
 * id never has to translate. Names follow the vendor's own casing — Housecall
 * Pro and Service Fusion write themselves that way, whatever the catalog API
 * returns.
 */
export const INTEGRATION_NAMES = {
  // FSM
  acculynx: "AccuLynx",
  aptive: "Aptive",
  buildops: "BuildOps",
  customcrm: "Custom FSM",
  fieldroutes: "FieldRoutes",
  housecallpro: "Housecall Pro",
  jobnimbus: "JobNimbus",
  maidcentral: "MaidCentral",
  realgreen: "RealGreen",
  servicefusion: "Service Fusion",
  servicetitan: "ServiceTitan",
  // Calendar
  google_calendar: "Google Calendar",
  outlook_calendar: "Outlook Calendar",
  // Lead platforms
  angi: "Angi",
  google_lsa: "Google Local Services",
  leadperfection: "LeadPerfection",
  thumbtack: "Thumbtack",
  // "Yelp leads" in the catalog: the connector handles leads, not the listing.
  yelp: "Yelp leads",
  // CRM
  hubspot: "HubSpot",
  salesforce: "Salesforce",
  watsco_oca: "Watsco OCA",
  // Telephony
  eight_by_eight: "8x8",
  voice_for_pest: "Voice for Pest",
  voice_for_pest_sip: "Voice for Pest — SIP trunk (AI Receptionist)",
  // Sameday's own books, not a company integration.
  quickbooks: "QuickBooks",
} as const;

type IntegrationNameKey = keyof typeof INTEGRATION_NAMES;

/**
 * Display name for an integration type. Falls back to whatever the caller has
 * — usually the catalog API's own spelling — so an integration we haven't
 * named yet still renders something rather than a raw id.
 */
export const integrationName = (type: string, fallback = ""): string =>
  INTEGRATION_NAMES[type as IntegrationNameKey] ?? fallback;
